import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import FormInput from '../../shared/ui/form/FormInput';
import Button from '../../shared/ui/button/Button';
import { GoogleLoginButton } from '../../components';
import { message } from 'antd';
import './AuthPages.css';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, register, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    preferredLanguage: 'ja' as 'zh' | 'ja' | 'en',
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 如果已经登录，重定向到首页
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [state.isAuthenticated, navigate]);

  // 清除错误信息
  useEffect(() => {
    if (state.error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [state.error, clearError]);

  // 表单验证
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('auth.registerPage.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('auth.registerPage.validation.emailInvalid');
    }

    if (!formData.displayName) {
      errors.displayName = t('auth.registerPage.validation.nameRequired');
    } else if (formData.displayName.length < 2) {
      errors.displayName = t('auth.registerPage.validation.nameRequired');
    }

    if (!formData.password) {
      errors.password = t('auth.registerPage.validation.passwordRequired');
    } else if (formData.password.length < 8) {
      errors.password = t('auth.registerPage.validation.passwordMinLength');
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = t('auth.registerPage.validation.passwordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('auth.registerPage.validation.passwordsNotMatch');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // 清除对应字段的错误
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await register(
        formData.email, 
        formData.password, 
        formData.displayName,
        formData.preferredLanguage
      );
      // 注册成功后会通过useEffect重定向
    } catch (error) {
      console.error('注册失败:', error);
      // 错误信息已经在AuthContext中处理
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">{t('auth.registerPage.title')}</h1>
          <p className="auth-subtitle">{t('auth.registerPage.subtitle')}</p>
        </div>

        {state.error && (
          <div className="auth-error">
            <span className="auth-error-icon">⚠️</span>
            {state.error}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <FormInput
            id="email"
            name="email"
            type="email"
            label={t('auth.email')}
            value={formData.email}
            onChange={handleInputChange}
            error={formErrors.email}
            placeholder={t('auth.registerPage.emailPlaceholder')}
            icon={<span>📧</span>}
            fullWidth
            required
          />

          <FormInput
            id="displayName"
            name="displayName"
            type="text"
            label={t('auth.registerPage.displayNamePlaceholder')}
            value={formData.displayName}
            onChange={handleInputChange}
            error={formErrors.displayName}
            placeholder={t('auth.registerPage.displayNamePlaceholder')}
            icon={<span>👤</span>}
            fullWidth
            required
          />

          <FormInput
            id="password"
            name="password"
            type="password"
            label={t('auth.password')}
            value={formData.password}
            onChange={handleInputChange}
            error={formErrors.password}
            placeholder={t('auth.registerPage.passwordPlaceholder')}
            icon={<span>🔒</span>}
            fullWidth
            required
          />

          <FormInput
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            label={t('auth.confirmPassword')}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            error={formErrors.confirmPassword}
            placeholder={t('auth.registerPage.confirmPasswordPlaceholder')}
            icon={<span>🔒</span>}
            fullWidth
            required
          />

          <div className="form-input-wrapper">
            <label className="form-input-label" htmlFor="preferredLanguage">
              {t('auth.registerPage.languageLabel')}
            </label>
            <select
              id="preferredLanguage"
              name="preferredLanguage"
              value={formData.preferredLanguage}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="ja">{t('auth.registerPage.japanese')}</option>
              <option value="zh">{t('auth.registerPage.chinese')}</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="auth-actions">
            <Button
              htmlType="submit"
              buttonVariant="primary"
              size="large"
              fullWidth
              loading={isSubmitting || state.loading}
              disabled={isSubmitting || state.loading}
            >
              {isSubmitting || state.loading ? t('common.loading') : t('auth.registerPage.registerButton')}
            </Button>
          </div>
        </form>

        <div className="auth-divider">
          <span>{t('common.or')}</span>
        </div>

        <div className="auth-social">
          <GoogleLoginButton
            text={t('auth.registerPage.googleRegister')}
            onSuccess={() => {
              message.success(t('auth.registerPage.registerSuccess'));
              // 注册成功后会通过useEffect重定向
            }}
            onError={(error) => {
              message.error(error);
            }}
          />
        </div>

        <div className="auth-footer">
          <p className="auth-footer-text">
            {t('auth.registerPage.hasAccount')}
            <Link to="/login" className="auth-link">
              {t('auth.registerPage.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
